import { Transfer, CbmImpl } from "~/modules/cbm";
import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { getReservationsByTask, sendTrackingEvent } from "~/modules/actions/ActionUtils";
import { EVENTS } from "~/modules/analytics/Analytics";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { ChannelNoteServiceImpl } from "~/backend/generated/Notes/api/channelNote.service";




export interface GetChannelTransfersOptions {
    withNotes?: boolean;
}




export interface ChannelTransfer {
    sid: string;
    channelSid: string;
    type: string;
    from: string;
    to: string;
    status: string;
    reason: string;
    dateCreated: Date;
    summary?: string;
}






















export class GetChannelTransfers implements Action<Promise<Array<Transfer | ChannelTransfer>>> {
    


    #taskSid: string;

    


    #options?: GetChannelTransfersOptions;

    constructor(taskSid: string, options?: GetChannelTransfersOptions) {
        this.#taskSid = taskSid;
        this.#options = options;
    }

    
    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const notesService = context.retrieveInstanceOf(
            ContextInstance.ChannelNotesServiceImpl,
            ChannelNoteServiceImpl
        );

        const accountConfigDataContainer = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );

        logger.debug(`getChannelTransfers invoked with taskSid: ${this.#taskSid}`);

        const task = getReservationsByTask(worker, this.#taskSid)[0]?.task;

        if (!task) {
            const errorMsg = `getChannelTransfers: Task not found for taskSid ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.GetChannelTransfersCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                withNotes: this.#options?.withNotes
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const interactionSid = task.attributes.flexInteractionSid;

        if (!interactionSid) {
            const errorMsg = `getChannelTransfers: Missing interactionSid for taskSid ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.GetChannelTransfersCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                withNotes: this.#options?.withNotes
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const channelSid = task.attributes.flexInteractionChannelSid;

        if (!channelSid) {
            const errorMsg = `getChannelTransfers: Missing channelSid for taskSid ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.GetChannelTransfersCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                withNotes: this.#options?.withNotes
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const instanceSid = accountConfigDataContainer.get().flexInstanceSid;

        if (!instanceSid) {
            const errorMsg = `getChannelTransfers: Missing instanceSid for taskSid ${this.#taskSid}`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.GetChannelTransfersCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                withNotes: this.#options?.withNotes
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
        try {
            const transfers = await cbm.getChannelTransfers(instanceSid, interactionSid, channelSid);

            if (!transfers) {
                sendTrackingEvent(EVENTS.GetChannelTransfersCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    withNotes: this.#options?.withNotes
                });

                return Promise.resolve([]);
            }
            const transfersWithNotes: Array<ChannelTransfer> = await Promise.all(
                transfers.map(async (transfer) => {
                    let summary = "";
                    let to = transfer.to;

                    if (this.#options?.withNotes) {
                        try {
                            if (transfer.noteSid) {
                                const note = await notesService.getChannelNote(instanceSid, transfer.noteSid);
                                summary = note?.summary || "";
                            }
                        } catch (e) {
                            const errorMsg = `getChannelTransfers: Failed to fetch notes for transfer for task ${
                                this.#taskSid
                            }`;
                            logger.error(errorMsg);
                        }
                    }
                    if (transfer.to.startsWith("WK")) {
                        try {
                            to = (await taskRouter.workspace.fetchWorker(transfer.to)).attributes?.full_name || "";
                        } catch (e) {
                            const errorMsg = `getChannelTransfers: Failed to fetch worker ${transfer.to} for task ${
                                this.#taskSid
                            }`;
                            logger.error(errorMsg);
                        }
                    }

                    if (transfer.to.startsWith("WQ")) {
                        try {
                            to = (await taskRouter.workspace.fetchTaskQueue(transfer.to)).name || "";
                        } catch (e) {
                            const errorMsg = `getChannelTransfers: Failed to fetch queue ${transfer.to} for task ${
                                this.#taskSid
                            }`;
                            logger.error(errorMsg);
                        }
                    }

                    return {
                        sid: transfer.sid,
                        channelSid: transfer.channelSid,
                        type: transfer.type,
                        from: transfer.from,
                        to,
                        status: transfer.status,
                        reason: transfer.reason,
                        dateCreated: new Date(transfer.dateCreated),
                        summary
                    };
                })
            );

            sendTrackingEvent(EVENTS.GetChannelTransfersCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                withNotes: this.#options?.withNotes
            });

            return Promise.resolve(
                transfersWithNotes.sort((a, b) => a.dateCreated.getTime() - b.dateCreated.getTime())
            );
        } catch (e) {
            const errorMsg = `getChannelTransfers: Failed to fetch transfers for conversation channel for taskSid ${
                this.#taskSid
            } : ${e.message}`;
            if (String(e.message.toLowerCase()).includes("forbidden")) {
                const customErrorMsg = `${errorMsg}: Check if messaging transfers are enabled in Twilio Console`;
                logger.error(customErrorMsg);
                sendTrackingEvent(EVENTS.GetChannelTransfersCompleted, analytics, worker, {
                    error: customErrorMsg,
                    taskSid: this.#taskSid,
                    withNotes: this.#options?.withNotes
                });
                return Promise.reject(
                    new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, customErrorMsg)
                );
            }

            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.GetChannelTransfersCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                withNotes: this.#options?.withNotes
            });

            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }
}
